import {
  useEffect, useMemo, useCallback, useState,
} from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { map } from '../core/MapView';
import useMapControls from './useMapControls';
import { useTranslation } from '../../common/components/LocalizationProvider';
import './mapControls.css';

/**
 * Controle customizado para MapLibre GL
 * Renderiza um painel vertical com toggles
 */
class MapControlsPanelControl {
  constructor(container) {
    this.container = container;
  }

  onAdd() {
    return this.container;
  }

  onRemove() {
    this.container.parentNode?.removeChild(this.container);
  }
}

/**
 * Painel de controles do mapa estilo GPSWox
 */
const MapControlsPanel = () => {
  const theme = useTheme();
  const t = useTranslation();
  const {
    settings, toggleSetting, fitBounds, availability, filterPanels, toggleFilterPanel,
  } = useMapControls();

  // 2ECOMP: PremiumUI Refresh — fullscreen state synced via body class
  const [isFullscreen, setIsFullscreen] = useState(() => document.body.classList.contains('map-fullscreen'));

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsFullscreen(document.body.classList.contains('map-fullscreen'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Container para o controle do mapa
  const controlContainer = useMemo(() => {
    const container = document.createElement('div');
    container.className = 'maplibregl-ctrl maplibregl-ctrl-group map-controls-panel';
    return container;
  }, []);

  // Controle do mapa
  const control = useMemo(
    () => new MapControlsPanelControl(controlContainer),
    [controlContainer]
  );

  // 2ECOMP: mobile — bottom-right no mobile, top-right no desktop
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'));

  // Adiciona/remove controle do mapa
  useEffect(() => {
    const position = isMobile
      ? 'bottom-right'
      : (theme.direction === 'rtl' ? 'top-left' : 'top-right');
    map.addControl(control, position);
    return () => {
      try {
        map.removeControl(control);
      } catch {
        // Ignora erro se o mapa já foi destruído
      }
    };
  }, [control, theme.direction, isMobile]);

  // 2ECOMP: PremiumUI Refresh — handler fullscreen via custom event
  const handleFullscreen = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.dispatchEvent(new CustomEvent('mapToggleFullscreen'));
  }, []);

  // Handler para fit bounds
  const handleFitBounds = useCallback(() => {
    // Remove foco do botão para evitar warning de aria-hidden
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    fitBounds();
  }, [fitBounds]);

  // Handler genérico para toggles
  const handleToggle = useCallback((key) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    toggleSetting(key);
  }, [toggleSetting]);

  // Handler para botões de filtro
  const handleFilterToggle = useCallback((filterType) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    toggleFilterPanel(filterType);
  }, [toggleFilterPanel]);

  // Definição dos controles - memoizado para evitar re-renders
  const controls = useMemo(() => [
    // 2ECOMP: PremiumUI Refresh — botão fullscreen como primeiro controle
    {
      id: 'fullscreen',
      icon: isFullscreen ? 'ctrl-fullscreen-exit' : 'ctrl-fullscreen',
      title: isFullscreen ? 'Restaurar layout' : 'Mapa tela cheia',
      isAction: true,
      isFullscreen: true,
    },
    {
      id: 'clustering',
      icon: 'ctrl-clustering',
      title: t('mapClustering') || 'Agrupamento',
      setting: 'clustering',
    },
    {
      id: 'fitBounds',
      icon: 'ctrl-fit-bounds',
      title: t('mapFitBounds') || 'Centralizar Todos',
      isAction: true,
    },
    {
      id: 'showDevices',
      icon: 'ctrl-devices',
      title: t('deviceTitle') || 'Dispositivos',
      setting: 'showDevices',
    },
    {
      id: 'showGeofences',
      icon: 'ctrl-geofences',
      title: t('sharedGeofences') || 'Geofences',
      setting: 'showGeofences',
    },
    {
      id: 'filterGeofence',
      icon: 'ctrl-filter-geofence',
      title: t('mapFilterGeofences') || 'Filtrar Geofences',
      isFilter: 'geofence',
    },
    {
      id: 'showRoutes',
      icon: 'ctrl-routes',
      title: t('reportRoute') || 'Rotas',
      setting: 'showRoutes',
      disabled: true, // TODO: implementar rotas salvas
    },
    {
      id: 'showPoi',
      icon: 'ctrl-poi',
      title: t('mapPoiLayer') || 'Pontos de Interesse',
      setting: 'showPoi',
      checkAvailability: 'showPoi',
    },
    {
      id: 'filterPoi',
      icon: 'ctrl-filter-poi',
      title: t('poiFilterGroups') || 'Filtrar POIs',
      isFilter: 'poi',
    },
    {
      id: 'showLabels',
      icon: 'ctrl-labels',
      title: t('mapShowLabels') || 'Mostrar Nomes',
      setting: 'showLabels',
    },
    {
      id: 'showTail',
      icon: 'ctrl-tail',
      title: t('mapLiveRoutes') || 'Rastro',
      setting: 'showTail',
    },
    {
      id: 'showTraffic',
      icon: 'ctrl-traffic',
      title: t('mapGoogleTraffic') || 'Tráfego ao Vivo',
      setting: 'showTraffic',
      checkAvailability: 'showTraffic',
    },
  ], [t, isFullscreen]); // Recria quando traduções ou fullscreen mudam

  // Renderiza os botões no container do controle
  useEffect(() => {
    // Limpa container
    controlContainer.innerHTML = '';

    // Renderiza cada botão
    controls.forEach((ctrl) => {
      // 2ECOMP: PremiumUI Refresh — em fullscreen, só mostra botão fullscreen
      if (isFullscreen && !ctrl.isFullscreen) return;

      // Verifica disponibilidade usando o mapa de availability
      if (ctrl.checkAvailability && !availability[ctrl.checkAvailability]) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = `maplibregl-ctrl-icon map-control-btn ${ctrl.icon}`;
      button.title = ctrl.title;
      button.setAttribute('aria-label', ctrl.title);

      if (ctrl.disabled) {
        button.classList.add('disabled');
        button.disabled = true;
      }

      if (ctrl.isFullscreen) {
        // 2ECOMP: PremiumUI Refresh — botão fullscreen
        if (isFullscreen) {
          button.classList.add('active');
        }
        button.setAttribute('aria-pressed', String(isFullscreen));
        button.onclick = handleFullscreen;
      } else if (ctrl.isAction) {
        // Botão de ação (não é toggle)
        button.onclick = handleFitBounds;
      } else if (ctrl.isFilter) {
        // Botão de filtro (abre/fecha painel)
        const isActive = filterPanels[ctrl.isFilter];
        if (isActive) {
          button.classList.add('active');
        }
        button.setAttribute('aria-pressed', String(isActive));
        button.onclick = () => handleFilterToggle(ctrl.isFilter);
      } else {
        // Botão toggle
        const isActive = settings[ctrl.setting];
        if (isActive) {
          button.classList.add('active');
        }
        button.setAttribute('aria-pressed', String(isActive));
        button.onclick = () => handleToggle(ctrl.setting);
      }

      controlContainer.appendChild(button);
    });
  }, [settings, availability, controls, controlContainer, handleToggle, handleFitBounds, handleFullscreen, isFullscreen, filterPanels, handleFilterToggle]);

  return null;
};

export default MapControlsPanel;
